import { z } from 'zod'

import { accountStatsSchema } from './accountStats'
import {
  addressSchema,
  dbEntitySchema,
  listQueryZodFields,
  queryBooleanSchema,
} from './system'
import {
  OrganizationPlatformType,
  OrganizationType,
} from '../constants/organization'
import { ExternalAccountKind } from '../constants/organizationRelationshipKind'
import { organizationMemberSchema } from './organizationMember'
import { venueCreateArgsSchema } from './venue'

export const organizationSchema = dbEntitySchema.extend({
  created: z.coerce.date(),
  modified: z.coerce.date(),
  name: z.string(),
  enabled: z.boolean().nullish(),
  isVenueOwner: z.boolean().nullish(),
  subscriptionActive: z.boolean().nullish(),
  activeSubscriptions: z.number().nullish(),
  payingNonSubscribedCount: z.number().nullish(),
  effectivePayingVenues: z.number().nullish(),
  type: z.nativeEnum(OrganizationType),
  isPromoter: z.boolean().nullish(),
  accountKind: z.nativeEnum(ExternalAccountKind).nullish(),
  logo: z.string().nullish(),
  stats: accountStatsSchema.nullish(),
  fullAddress: addressSchema.nullish(),
  /**
   * @deprecated - use fullAddress.address
   */
  address: z.string().nullish(),
  /**
   * @deprecated - use fullAddress.city
   */
  city: z.string().nullish(),
  /**
   * @deprecated - use fullAddress.administrativeArea
   */
  state: z.string().nullish(),
  /**
   * @deprecated - use fullAddress.postalCode
   */
  zip: z.string().nullish(),
  /**
   * @deprecated - use fullAddress.country
   */
  country: z.string().nullish(),
})

export const organizationCreateBodySchema = organizationSchema
  .omit({
    id: true,
    _id: true,
    created: true,
    modified: true,
    deleted: true,
    enabled: true,
    isVenueOwner: true,
    isPromoter: true,
    type: true,
    city: true,
    state: true,
    zip: true,
    country: true,
  })
  .extend({
    type: z.nativeEnum(OrganizationType).optional(),
  })

export const organizationManagementUserCreateBodySchema =
  organizationCreateBodySchema
    .omit({
      stats: true,
    })
    .extend({
      email: z.string(),
      accountKind: z.nativeEnum(ExternalAccountKind).nullish(),
      logo: z.string().nullish(),
      venueData: venueCreateArgsSchema
        .omit({
          organizationId: true,
        })
        .nullish(),
    })

export const organizationUpdateByIdBodySchema = organizationSchema
  .partial()
  .pick({
    fullAddress: true,
    name: true,
    enabled: true,
    isPromoter: true,
    logo: true,
  })

export const orgsListQuerySchema = z.object({
  ...listQueryZodFields,
  id: z.string().or(z.array(z.string())).nullish(),
  type: z.nativeEnum(OrganizationType).nullish(),
  accountKind: z.nativeEnum(ExternalAccountKind).nullish(),
  country: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  enabled: queryBooleanSchema.nullish(),
  isVenueOwner: queryBooleanSchema.nullish(),
  isPromoter: queryBooleanSchema.nullish(),
  subscriptionActive: queryBooleanSchema.nullish(),
  search: z.string().nullish(),
  healthScoreMin: z.number().nullish(),
  totalVisitsMin: z.number().nullish(),
})

export const orgsListResponseSchema = z.object({
  data: z.array(organizationSchema),
  count: z.number(),
})

/** Embedded on GET /v1/management-users/:id/organizations; admin users may get a synthetic member without id. */
export const organizationMemberEmbeddedSchema = organizationMemberSchema.extend(
  {
    id: z.string().optional(),
    _id: z.string().optional(),
    deleted: z.boolean().optional(),
    created: z.coerce.date().optional(),
    modified: z.coerce.date().optional(),
  }
)

export const organizationWithMemberSchema = organizationSchema.extend({
  organizationMember: organizationMemberEmbeddedSchema.optional().nullable(),
})

export const organizationsByManagementUserListResponseSchema = z.object({
  data: z.array(organizationWithMemberSchema),
})
